/**
 * 按文件身份记住按钮选择器返回的句柄。antd 收下的 File 和句柄读出的 File 不是同一个对象。
 * 拖拽只读取普通 File，不获取文件句柄；刷新后可重新选择原文件续传。
 */
const handlesByIdentity = new Map();
/** 给上传列表条目分配不重复的 uid。 */
let uploadFileSerial = 0;
/**
 * 用文件名、大小和修改时间标识同一个本地文件。
 * @param file 本地文件或其身份
 * @returns 句柄表的键
 */
export function uploadFileIdentityKey(file) {
    return `${file.name}\u0000${String(file.size)}\u0000${String(file.lastModified)}`;
}
/** 当前浏览器能否在用户点击时拿到可持久化的文件句柄。 */
export function canPickUploadFileHandles() {
    return typeof window !== 'undefined' && typeof readPickerWindow().showOpenFilePicker === 'function';
}
/**
 * 记住按钮选择器返回的句柄，供入队时写入 IndexedDB。
 * @param file 句柄读出的文件，用来对齐稍后进入列表的 File
 * @param handle 浏览器文件句柄
 */
export function rememberUploadFileHandle(file, handle) {
    handlesByIdentity.set(uploadFileIdentityKey(file), handle);
}
/**
 * 按文件身份取回本次页面记住的句柄。
 * @param file 即将上传的文件
 * @returns 句柄；这次选择没有拿到时为空
 */
export function uploadFileHandleOf(file) {
    return handlesByIdentity.get(uploadFileIdentityKey(file)) ?? null;
}
/**
 * 在用户点击里打开系统文件选择器，并记住每个文件的句柄。
 * 用户取消时返回空数组。
 * @returns 选中的文件
 */
export async function pickUploadFiles() {
    const picker = readPickerWindow().showOpenFilePicker;
    if (!picker)
        return [];
    try {
        const handles = await picker({ multiple: true });
        /** 选择器返回的文件，已和句柄按身份对齐。 */
        const files = [];
        for (const handle of handles) {
            const file = await handle.getFile();
            rememberUploadFileHandle(file, handle);
            files.push(file);
        }
        return files;
    }
    catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError')
            return [];
        return [];
    }
}
/**
 * 在用户点击重试时申请权限并读回原文件。
 * 权限被拒绝、文件已移动或句柄失效时返回空，由调用方改回选择原文件。
 * @param handle IndexedDB 里保存的句柄
 * @returns 原文件；无法读取时为空
 */
export async function readGrantedUploadFile(handle) {
    try {
        if (handle.requestPermission) {
            const state = await handle.requestPermission({ mode: 'read' });
            if (state !== 'granted')
                return null;
        }
        return await handle.getFile();
    }
    catch {
        return null;
    }
}
/**
 * 把文件收成 antd 上传列表条目，并保留原始 File 作为上传对象。
 * @param file 已选文件
 * @returns 上传列表条目
 */
export function asUploadListFile(file) {
    uploadFileSerial += 1;
    /** 列表项标识，只用于本次弹窗内的增删。 */
    const uid = `upload-${String(file.lastModified)}-${String(uploadFileSerial)}`;
    const origin = Object.assign(file, { uid });
    return {
        uid,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'done',
        originFileObj: origin,
    };
}
/** 读取可能带文件选择器的窗口对象。 */
function readPickerWindow() {
    return window;
}
